import React, { useCallback, useEffect, useMemo, useState } from "react";

import Svg from "@/styles/utils/Svg";
import { isTouchDevice } from "@/utils/getters";

import Counter from "./Counter";
import {
  Circle,
  CounterWrapper,
  IconWrapper,
  InnerCircle,
  MoreInfo,
  ResultCircle,
  ResultWrapper,
  Share,
  Text,
  Water,
} from "./styles";

export enum ResultType {
  percent = "percent",
  count = "count",
}

export enum SharingType {
  x = "x",
  whatsApp = "whatsapp",
  facebook = "facebook",
}

export type ResultGlassProps = {
  type: ResultType;
  icons: IIcons;
  resultText?: string;
  userVoteCount: number;
  totalVoteCount: number;
  showDetails: boolean;
  onShareClick: (type: SharingType) => void;
};

const ResultGlass: React.FC<ResultGlassProps> = ({
  type,
  icons,
  resultText,
  userVoteCount,
  totalVoteCount,
  showDetails,
  onShareClick,
}) => {
  const [animation, setAnimation] = useState(false);

  const percent = useMemo(
    () => Math.round((userVoteCount / totalVoteCount) * 100),
    [userVoteCount, totalVoteCount]
  );

  const isTouchDeviceValue = useMemo(() => isTouchDevice(), []);

  useEffect(() => {
    // Get another keyframe
    const timer = setTimeout(() => setAnimation(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  const renderCount = useCallback((): React.JSX.Element => {
    if (type === ResultType.count) {
      return (
        <React.Fragment>
          {animation && (
            <CounterWrapper>
              <h1>
                {userVoteCount} / {totalVoteCount}
              </h1>
            </CounterWrapper>
          )}
        </React.Fragment>
      );
    } else {
      return (
        <React.Fragment>
          {animation && (
            <CounterWrapper>
              <Counter start={0} end={percent} duration={2000}>
                {(value: number) => <span>{value || 0}%</span>}
              </Counter>
            </CounterWrapper>
          )}
        </React.Fragment>
      );
    }
  }, [animation, type, userVoteCount, totalVoteCount, percent]);

  return (
    <Circle>
      <InnerCircle>
        <Water percent={animation ? percent : 0} />
        {animation && (
          <ResultWrapper>
            <ResultCircle>
              {renderCount()}
              <MoreInfo>
                {showDetails && (
                  <Text>
                    <span>{userVoteCount.toLocaleString()}</span> von{" "}
                    <span>{totalVoteCount.toLocaleString()}</span>
                  </Text>
                )}
                {resultText && <Text>{resultText}</Text>}
                <Share>
                  <IconWrapper
                    onClick={() => onShareClick(SharingType.facebook)}
                  >
                    <Svg contents={icons.Facebook} height={20} width={25} />
                  </IconWrapper>
                  <IconWrapper onClick={() => onShareClick(SharingType.x)}>
                    <Svg contents={icons.X} height={22} width={22} />
                  </IconWrapper>
                  {isTouchDeviceValue && (
                    <IconWrapper
                      onClick={() => onShareClick(SharingType.whatsApp)}
                    >
                      <Svg contents={icons.WhatsApp} height={22} width={22} />
                    </IconWrapper>
                  )}
                </Share>
              </MoreInfo>
            </ResultCircle>
          </ResultWrapper>
        )}
      </InnerCircle>
    </Circle>
  );
};

export default ResultGlass;
